import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchFeaturedProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/products?limit=1000`);
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/products/featured/list`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  const toggleFeatured = (productId) => {
    const isCurrentlyFeatured = featuredProducts.find(p => p._id === productId);
    
    if (isCurrentlyFeatured) {
      // Remove from featured
      setFeaturedProducts(featuredProducts.filter(p => p._id !== productId));
    } else {
      // Add to featured (max 10)
      if (featuredProducts.length >= 10) {
        setMessage('Maximum 10 featured products allowed!');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      
      const product = products.find(p => p._id === productId);
      if (product) {
        setFeaturedProducts([...featuredProducts, { ...product, isFeatured: true }]);
      }
    }
  };

  const reorderFeatured = (dragIndex, hoverIndex) => {
    const draggedProduct = featuredProducts[dragIndex];
    const newFeaturedProducts = [...featuredProducts];
    newFeaturedProducts.splice(dragIndex, 1);
    newFeaturedProducts.splice(hoverIndex, 0, draggedProduct);
    setFeaturedProducts(newFeaturedProducts);
  };

  const saveFeaturedProducts = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const productIds = featuredProducts.map(p => p._id);
      await axios.post(`${config.API_URL}/products/featured/update`, {
        featuredProducts: productIds
      });
      
      setMessage('Featured products updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving featured products:', error);
      setMessage('Error saving featured products');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="featured-products-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>🏆 Featured Products Management</h2>
        <button 
          className="btn btn-primary"
          onClick={saveFeaturedProducts}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('successfully') ? 'alert-success' : 'alert-danger'} alert-dismissible fade show`}>
          {message}
        </div>
      )}

      <div className="row">
        {/* Featured Products Section */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Featured Products ({featuredProducts.length}/10)</h5>
            </div>
            <div className="card-body" style={{ minHeight: '400px' }}>
              {featuredProducts.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-star fs-1"></i>
                  <p>No featured products selected</p>
                </div>
              ) : (
                <div className="featured-products-list">
                  {featuredProducts.map((product, index) => (
                    <div key={product._id} className="featured-product-item">
                      <div className="product-info">
                        <div className="product-image">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} />
                          ) : (
                            <div className="no-image">📱</div>
                          )}
                        </div>
                        <div className="product-details">
                          <h6>{product.name}</h6>
                          <p className="text-muted mb-1">{product.brand}</p>
                          <p className="mb-0">₹{product.offerPrice || product.price}</p>
                        </div>
                      </div>
                      <div className="product-actions">
                        <span className="order-badge">{index + 1}</span>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => toggleFeatured(product._id)}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* All Products Section */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">All Products</h5>
            </div>
            <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <div className="all-products-list">
                {products.filter(p => !featuredProducts.find(fp => fp._id === p._id)).map(product => (
                  <div key={product._id} className="all-product-item">
                    <div className="product-info">
                      <div className="product-image">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} />
                        ) : (
                          <div className="no-image">📱</div>
                        )}
                      </div>
                      <div className="product-details">
                        <h6>{product.name}</h6>
                        <p className="text-muted mb-1">{product.brand} • {product.category}</p>
                        <p className="mb-0">₹{product.offerPrice || product.price}</p>
                      </div>
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => toggleFeatured(product._id)}
                      disabled={featuredProducts.length >= 10}
                    >
                      <i className="bi bi-plus"></i> Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .featured-products-management {
          padding: 20px;
        }
        
        .featured-product-item,
        .all-product-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          margin-bottom: 8px;
          background: white;
        }
        
        .product-info {
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .product-image {
          width: 50px;
          height: 50px;
          margin-right: 12px;
          border-radius: 6px;
          overflow: hidden;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .no-image {
          font-size: 20px;
        }
        
        .product-details h6 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        .product-details p {
          margin: 0;
          font-size: 12px;
        }
        
        .order-badge {
          background: #007bff;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          margin-right: 8px;
        }
        
        .product-actions {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
}

export default FeaturedProducts;
